// Shared interfaces for the entire application

export interface SoundEffect {
    id: string;
    title: string;
    duration: number;
    prompt: string;
    promptInfluence?: number;
    thumbnail: {
        color: string;
        emoji: string;
    };
    folderId?: string;
    createdAt: string;
    downloadUrl: string;
    // Additional fields from API response
    filename?: string;
    size?: number;
    uploaded?: string;
}

export interface Project {
    id: string;
    name: string;
    created_at?: string;
    user_id?: string;
}

export interface Folder {
    id: string;
    name: string;
    project_id: string;
    parent_folder_id: string | null;
    created_at: string;
    user_id: string;
    icon?: string;
}

export interface FolderType {
    id: string;
    name: string;
    icon: string;
    sounds: string[];
}

export interface Subscription {
    id?: string;
    user_id?: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    status: 'free' | 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
    plan_type: 'free' | 'pro';
    current_period_start?: string;
    current_period_end?: string;
    cancel_at_period_end?: boolean;
    created_at?: string;
    updated_at?: string;
}

// Helper function to convert API response to UI SoundEffect
export function convertApiResponseToSoundEffect(apiSound: any): SoundEffect {
    // Extract promptInfluence from generation_params if available
    const generationParams = apiSound.generation_params || {};
    const promptInfluence = generationParams.promptInfluence;
    
    return {
        id: apiSound.id,
        title: apiSound.name || 'Untitled',
        duration: apiSound.duration_seconds || 7.0,
        prompt: apiSound.description || apiSound.elevenlabs_prompt || 'No description',
        promptInfluence,
        thumbnail: getRandomThumbnail(),
        folderId: apiSound.folder_id,
        createdAt: apiSound.created_at,
        downloadUrl: apiSound.downloadUrl,
        // Keep original fields for compatibility
        filename: `${apiSound.name || 'untitled'}.${apiSound.format || 'mp3'}`,
        size: apiSound.file_size,
        uploaded: apiSound.created_at
    };
}

// Helper function for generating random thumbnails
function getRandomThumbnail() {
    const colors = ['bg-amber-200', 'bg-blue-200', 'bg-green-200', 'bg-purple-200', 'bg-red-200'];
    const emojis = ['🎵', '🌊', '🔥', '💨', '⚡', '🌧️', '🎶', '🥁', '🎸', '🚗'];
    return {
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: emojis[Math.floor(Math.random() * emojis.length)]
    };
}

// Export configuration types
export interface ExportConfig {
    // Audio format settings
    format: 'wav' | 'mp3' | 'ogg' | 'flac' | 'original';
    sampleRate: 22050 | 44100 | 48000 | 96000 | 'original';
    bitDepth: 8 | 16 | 24 | 32 | 'original';
    channels: 'mono' | 'stereo' | 'original';
    bitrate?: number; // For MP3/OGG (e.g., 128, 192, 320)
    
    // Audio processing
    normalization: 'none' | 'peak' | 'lufs';
    targetLufs?: number; // -23 for broadcast, -16 for streaming
    trimSilence: boolean;
    silenceThreshold?: number; // in dB, e.g., -40
    fadeIn?: number; // milliseconds
    fadeOut?: number; // milliseconds
    
    // File naming
    namingPattern: string; // e.g., "{category}_{name}_{index}"
    sanitizeFilenames: boolean;
    replaceSpaces: '_' | '-' | 'remove' | 'keep';
    caseConversion: 'lower' | 'upper' | 'camel' | 'snake' | 'none';
    indexPadding: number; // e.g., 3 for 001, 002
    
    // Organization
    folderStructure: 'flat' | 'category' | 'tags' | 'custom';
    customFolderPattern?: string; // e.g., "{category}/{subcategory}"
    maxFilesPerFolder?: number;
    
    // Metadata
    includeMetadata: boolean;
    metadataFormat: 'json' | 'xml' | 'csv' | 'txt';
    metadataFields: string[]; // Which fields to include
}

export interface SavedExportConfig {
    id: string;
    name: string;
    description?: string;
    config: ExportConfig;
    isPublic: boolean;
    usageCount: number;
    createdAt: string;
    isDefault?: boolean;
}

export interface ExportJob {
    id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    config: ExportConfig;
    soundIds: string[];
    resultUrl?: string;
    errorMessage?: string;
    progress?: {
        current: number;
        total: number;
        currentFile?: string;
    };
} 